/**
 * WB4 LLM-PALETTE — Map suggest API payload → palette ResolvedCommand[].
 *
 * Uses resolveAiSuggestedActionsFromIds SSOT + operational filter handler refs.
 * Never auto-executes — disclosure + requiresHumanApproval enforced at gateway.
 */

import type { WorkbenchCommandContext, ResolvedCommand } from './command-resolver';
import { resolveAiSuggestedActionsFromIds } from './ai-suggested-action-resolver';
import {
  resolveKernelActionPaletteGroup,
  buildExplainPaletteHandlerRef,
} from './workbench-command-palette-contract';
import { buildOperationalFilterResolvedCommand } from './workbench-palette-operational-query';
import { resolvePaletteOperationalFilterSpec } from './workbench-palette-operational-filter';
import { resolveFocusIntent, resolveWorkbenchActions } from './workbench-kernel';
import type { WorkbenchFocus } from './workbench-focus';

export interface PaletteLlmSuggestPayload {
  suggestedActionIds?: readonly string[];
  filterHandlerRef?: string;
  interpretationSummary?: string;
  stub?: boolean;
}

export const PALETTE_LLM_DISCLOSURE_COMMAND_ID = 'palette-llm-disclosure';

export function resolvePaletteLlmDisclosureLabel(stub?: boolean): string {
  return stub ? '규칙 파일럿 제안 (LLM 미연결)' : 'AI 자연어 해석 제안';
}

function resolveRegistryActionsForPalette(ctx: WorkbenchCommandContext) {
  if (!ctx.object) {
    return [];
  }

  const businessDomain = ctx.businessDomain ?? String(ctx.object.businessDomain ?? 'entity');
  const status = ctx.status ?? String(ctx.object.status);
  const focus: WorkbenchFocus =
    ctx.focus ??
    ({
      root: {
        id: ctx.object.id,
        businessDomain,
        ...(ctx.object.name ? { label: ctx.object.name } : {}),
      },
      selection: [],
      intent: resolveFocusIntent({
        role: ctx.role,
        status,
        plane: ctx.plane,
        permissionKeys: ctx.permissionKeys,
      }),
      mode: 'view',
      variant: 'object',
      actionProjection: 'commandPalette',
    } as WorkbenchFocus);

  return resolveWorkbenchActions(
    ctx.object,
    {
      role: ctx.role,
      plane: ctx.plane,
      permissionKeys: ctx.permissionKeys,
      surface: 'command',
      businessDomain,
      status,
      focus,
    },
    { permissionKeys: ctx.permissionKeys }
  );
}

export function resolvePaletteLlmCommands(
  payload: PaletteLlmSuggestPayload,
  ctx: WorkbenchCommandContext
): ResolvedCommand[] {
  const commands: ResolvedCommand[] = [];
  const seenHandlerRefs = new Set<string>();

  if (payload.filterHandlerRef?.trim()) {
    const spec = resolvePaletteOperationalFilterSpec(payload.filterHandlerRef.trim());
    if (spec) {
      commands.push(buildOperationalFilterResolvedCommand(spec, ctx));
      seenHandlerRefs.add(spec.handlerRef);
    }
  }

  if (payload.suggestedActionIds?.length) {
    const registryActions = resolveRegistryActionsForPalette(ctx);
    const { resolved } = resolveAiSuggestedActionsFromIds(
      payload.suggestedActionIds,
      registryActions
    );

    for (const action of resolved) {
      const handlerRef = action.handlerRef?.trim();
      if (!handlerRef || seenHandlerRefs.has(handlerRef)) {
        continue;
      }
      seenHandlerRefs.add(handlerRef);
      commands.push({
        id: `palette-llm-${action.id}`,
        label: action.label,
        section: 'action',
        kind: 'action',
        group: resolveKernelActionPaletteGroup(action),
        handlerRef,
        priority: 95,
        ...(action.availability === 'disabled' || action.forbidden ? { disabled: true } : {}),
        ...(action.reason?.trim() ? { reason: action.reason.trim() } : {}),
      });
    }
  }

  return commands;
}

export function buildPaletteLlmDisclosureCommand(
  payload: PaletteLlmSuggestPayload
): ResolvedCommand | undefined {
  const summary = payload.interpretationSummary?.trim();
  if (!summary) {
    return undefined;
  }

  return {
    id: PALETTE_LLM_DISCLOSURE_COMMAND_ID,
    label: resolvePaletteLlmDisclosureLabel(payload.stub),
    section: 'action',
    kind: 'action',
    group: 'explain',
    handlerRef: buildExplainPaletteHandlerRef(PALETTE_LLM_DISCLOSURE_COMMAND_ID),
    priority: 110,
    reason: summary,
  };
}

export function mergePaletteLlmIntoGrouped<
  T extends { grouped: import('./workbench-command-palette-contract').PaletteGroupedCommands },
>(base: T, llmCommands: readonly ResolvedCommand[], disclosure?: ResolvedCommand): T {
  if (llmCommands.length === 0 && !disclosure) {
    return base;
  }

  const grouped = { ...base.grouped };
  for (const group of Object.keys(grouped) as Array<keyof typeof grouped>) {
    grouped[group] = [...grouped[group]];
  }

  if (disclosure) {
    grouped.explain = [
      disclosure,
      ...grouped.explain.filter((command) => command.id !== PALETTE_LLM_DISCLOSURE_COMMAND_ID),
    ];
  }

  for (const command of llmCommands) {
    const group = command.group ?? 'act';
    const bucket = grouped[group];
    const duplicate =
      bucket.some((existing) => existing.id === command.id) ||
      bucket.some(
        (existing) =>
          (existing.handlerRef ?? existing.actionRef) === (command.handlerRef ?? command.actionRef)
      );
    if (!duplicate) {
      grouped[group] = [command, ...bucket];
    }
  }

  return { ...base, grouped };
}
